import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, BACKEND } from "@/lib/api";
import { Plus, Trash2, Pencil, X, Save, UploadCloud, Loader2 } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const empty = { url: "", alt: "", category: "interieur", order: 0 };
const CATS = ["interieur", "terrasse", "plats", "evenements", "general"];

export default function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [filter, setFilter] = useState("");
  const [bulk, setBulk] = useState({ progress: 0, total: 0, errors: [], running: false });
  const bulkInputRef = useRef();

  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...edit, order: Number(edit.order) || 0 };
    if (edit.id) await api.put(`/gallery/${edit.id}`, payload);
    else await api.post("/gallery", payload);
    setEdit(null); load();
  };
  const del = async (id) => {
    if (window.confirm("Supprimer cette image ?")) {
      await api.delete(`/gallery/${id}`); load();
    }
  };

  const onBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > 200) {
      alert("Maximum 200 fichiers à la fois.");
      return;
    }
    const defaultCat = window.prompt(
      `Catégorie par défaut pour ces ${files.length} photos ?\n(${CATS.join(", ")})`,
      "interieur"
    );
    if (!defaultCat || !CATS.includes(defaultCat)) return;

    setBulk({ progress: 0, total: files.length, errors: [], running: true });
    let uploaded = 0;
    const errors = [];
    const startOrder = items.length + 1;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        // 1. Upload
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // 2. Create gallery entry
        await api.post("/gallery", {
          url: up.data.url,
          alt: file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "),
          category: defaultCat,
          order: startOrder + i,
        });
        uploaded += 1;
      } catch (err) {
        errors.push({ name: file.name, error: formatApiErrorDetail(err.response?.data?.detail) || err.message });
      }
      setBulk((b) => ({ ...b, progress: i + 1, errors }));
    }
    setBulk((b) => ({ ...b, running: false }));
    await load();
    if (errors.length > 0) {
      alert(`${uploaded} photos ajoutées · ${errors.length} en erreur. Détails dans la console.`);
      console.warn("Bulk upload errors:", errors);
    }
    // Reset the file input so user can re-upload immediately
    if (bulkInputRef.current) bulkInputRef.current.value = "";
  };

  const filtered = filter ? items.filter((g) => g.category === filter) : items;

  return (
    <div data-testid="admin-gallery-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4 items-start sm:items-end">
        <div>
          <p className="overline">Médias</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Galerie</h1>
          <p className="text-[#697585] mt-2">{items.length} photo{items.length > 1 ? "s" : ""} au total.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            ref={bulkInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            className="hidden"
            onChange={onBulkUpload}
            data-testid="gallery-bulk-input"
          />
          <button
            data-testid="gallery-bulk-btn"
            onClick={() => bulkInputRef.current?.click()}
            disabled={bulk.running}
            className="btn-ghost"
          >
            {bulk.running ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
            Upload en masse
          </button>
          <button
            data-testid="add-gallery-btn"
            onClick={() => setEdit({ ...empty, order: items.length + 1 })}
            className="btn-primary"
          >
            <Plus size={14} /> Ajouter une photo
          </button>
        </div>
      </div>

      {bulk.running && (
        <div data-testid="bulk-progress" className="mt-6 border border-[#B8956A]/40 bg-[#B8956A]/5 p-5">
          <div className="flex justify-between items-center mb-3">
            <p className="overline text-[#203240]">Upload en cours</p>
            <p className="text-sm text-[#697585]">{bulk.progress} / {bulk.total}</p>
          </div>
          <div className="h-2 bg-[#D9D3C5] overflow-hidden">
            <div
              className="h-full bg-[#B8956A] transition-all"
              style={{ width: `${(bulk.progress / Math.max(1, bulk.total)) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-8 flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter("")}
          className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border ${filter === "" ? "border-[#B8956A] bg-[#B8956A]/10 text-[#203240]" : "border-[#D9D3C5] text-[#697585]"}`}
        >Toutes ({items.length})</button>
        {CATS.map((c) => {
          const count = items.filter((i) => i.category === c).length;
          return (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border ${filter === c ? "border-[#B8956A] bg-[#B8956A]/10 text-[#203240]" : "border-[#D9D3C5] text-[#697585]"}`}
            >{c} ({count})</button>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map((g) => (
          <div key={g.id} className="border border-[#D9D3C5] bg-[#FFFFFF] overflow-hidden">
            <div className="aspect-square">
              <img src={g.url?.startsWith("/api/") ? `${BACKEND}${g.url}` : g.url} alt={g.alt} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-3 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-widest text-[#B8956A]">{g.category}</p>
                <p className="text-xs text-[#697585] truncate">{g.alt}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setEdit(g)} className="p-2 hover:text-[#B8956A]"><Pencil size={12}/></button>
                <button onClick={() => del(g.id)} className="p-2 hover:text-red-400"><Trash2 size={12}/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <Modal title={edit.id ? "Modifier l'image" : "Nouvelle image"} onClose={() => setEdit(null)}>
          <div className="space-y-5">
            <ImageUploader label="Image" value={edit.url} onChange={(url) => setEdit({ ...edit, url })} />
            <input className="field" placeholder="Description (alt)" value={edit.alt} onChange={(e) => setEdit({ ...edit, alt: e.target.value })} data-testid="gallery-alt" />
            <select className="field" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} data-testid="gallery-cat">
              {CATS.map((c) => <option key={c} value={c} className="bg-[#F6F0E7]">{c}</option>)}
            </select>
            <input type="number" className="field" placeholder="Ordre" value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} />
          </div>
          <div className="mt-8 flex gap-3 justify-end">
            <button onClick={() => setEdit(null)} className="btn-ghost">Annuler</button>
            <button onClick={save} data-testid="gallery-save" className="btn-primary"><Save size={14}/> Enregistrer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FFFFFF] border border-[#D9D3C5] w-full max-w-xl p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <h2 className="font-display text-2xl">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
